import { memo, useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  UploadOutlined,
  SearchOutlined,
  BgColorsOutlined,
  ClockCircleOutlined,
  VideoCameraOutlined,
  ThunderboltOutlined,
  RobotOutlined,
  QuestionCircleOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import {
  AnimationContainer,
  ContentWrapper,
  OrbitalContainer,
  OrbitalRing,
  RingDot,
  VisualSceneWrapper,
  DocCard,
  DocTrafficLights,
  DocDot,
  DocLine,
  ChatContainer,
  ChatBubble,
  ProfileCard,
  ProfileAvatar,
  ProfileStatusDot,
  ProfileLine,
  ChartContainer,
  ChartBar,
  ParticlesContainer,
  Particle,
  StepCarouselContainer,
  StepItem,
  StepHighlight,
  StepIconWrapper,
  StepLabel,
  ActiveDots,
  ActiveDot,
  ProgressDotsContainer,
  ProgressDot,
} from "./LoadingAnimation.styled";

// ─── Visual Scenes ──────────────────────────────────────────

const DocumentVisual = () => (
  <DocCard>
    <DocTrafficLights>
      <DocDot $color="#FF6058" />
      <DocDot $color="#FFC130" />
      <DocDot $color="#27CA40" />
    </DocTrafficLights>
    <DocLine $width="100%" $delay={0} />
    <DocLine $width="75%" $delay={0.3} />
    <DocLine $width="85%" $delay={0.6} />
    <DocLine $width="60%" $delay={0.9} />
    <DocLine $width="90%" $delay={1.2} />
    <DocLine $width="50%" $delay={1.5} />
  </DocCard>
);

const ChatVisual = () => (
  <ChatContainer>
    <ChatBubble
      $align="left"
      $color="linear-gradient(135deg, rgb(129, 140, 248), rgb(168, 85, 247))"
      $width="75%"
    />
    <ChatBubble $align="right" $color="rgba(255, 255, 255, 0.9)" $width="60%" />
    <ChatBubble
      $align="left"
      $color="linear-gradient(135deg, rgb(168, 85, 247), rgb(236, 72, 153))"
      $width="85%"
    />
    <ChatBubble $align="right" $color="rgba(255, 255, 255, 0.9)" $width="50%" />
    <ChatBubble
      $align="left"
      $color="linear-gradient(135deg, rgb(129, 140, 248), rgb(99, 102, 241))"
      $width="65%"
    />
  </ChatContainer>
);

const ProfileVisualScene = () => (
  <ProfileCard>
    <ProfileAvatar>
      <ProfileStatusDot />
    </ProfileAvatar>
    <ProfileLine $width="70%" />
    <ProfileLine $width="50%" />
    <ProfileLine $width="85%" />
  </ProfileCard>
);

const ChartVisual = () => (
  <ChartContainer>
    <ChartBar $height="40%" $color="rgb(99, 102, 241)" $delay={0} />
    <ChartBar $height="65%" $color="rgb(129, 140, 248)" $delay={0.2} />
    <ChartBar $height="45%" $color="rgb(139, 92, 246)" $delay={0.4} />
    <ChartBar $height="80%" $color="rgb(99, 102, 241)" $delay={0.6} />
    <ChartBar $height="55%" $color="rgb(129, 140, 248)" $delay={0.8} />
    <ChartBar $height="70%" $color="rgb(139, 92, 246)" $delay={1.0} />
    <ChartBar $height="35%" $color="rgb(99, 102, 241)" $delay={1.2} />
  </ChartContainer>
);

const VISUAL_SCENES = [
  DocumentVisual,
  ChatVisual,
  ProfileVisualScene,
  ChartVisual,
];
const VISUAL_INTERVAL_MS = 6000;

const VISUAL_TRANSITION = {
  duration: 0.9,
  ease: [0.23, 1, 0.32, 1] as const,
};

// ─── Steps ──────────────────────────────────────────────────

const STEPS = [
  {
    label: "Uploading and validating your document",
    icon: UploadOutlined,
    duration: 3000,
  },
  {
    label: "Analyzing content and industry",
    icon: SearchOutlined,
    duration: 4500,
  },
  {
    label: "Creating a branded visual experience",
    icon: BgColorsOutlined,
    duration: 4500,
  },
  {
    label: "Planning video duration and scenes",
    icon: ClockCircleOutlined,
    duration: 4500,
  },
  {
    label: "Assembling scenes and transitions",
    icon: VideoCameraOutlined,
    duration: 4500,
  },
  {
    label: "Rendering and optimizing the final video",
    icon: ThunderboltOutlined,
    duration: 5500,
  },
  { label: "Training the AI chatbot", icon: RobotOutlined, duration: 4500 },
  {
    label: "Preparing suggested questions",
    icon: QuestionCircleOutlined,
    duration: 4500,
  },
  {
    label: "Preparing the experience for sharing",
    icon: ShareAltOutlined,
    duration: 4500,
  },
];

// ─── Particles config ────────────────────────────────────────

interface ParticleConfig {
  left: number;
  delay: number;
  duration: number;
  size: number;
}

const PARTICLES: ParticleConfig[] = Array.from({ length: 20 }, () => ({
  left: Math.random() * 100,
  delay: Math.random() * 6,
  duration: 6 + Math.random() * 4,
  size: 2 + Math.random() * 4,
}));

// ─── Component ──────────────────────────────────────────────

export interface LoadingAnimationProps {
  isVisible: boolean;
}

export const LoadingAnimation = memo(({ isVisible }: LoadingAnimationProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [activeVisual, setActiveVisual] = useState(0);

  // Step cycling — variable timing per step
  const advanceStep = useCallback(() => {
    setActiveStep((prev) => (prev + 1) % STEPS.length);
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const timeout = setTimeout(advanceStep, STEPS[activeStep].duration);
    return () => clearTimeout(timeout);
  }, [isVisible, activeStep, advanceStep]);

  // Visual cycling — fixed 6s interval
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setActiveVisual((prev) => (prev + 1) % VISUAL_SCENES.length);
    }, VISUAL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isVisible]);

  const particles = useMemo(() => PARTICLES, []);

  const visibleSteps = useMemo(() => {
    const prev = (activeStep - 1 + STEPS.length) % STEPS.length;
    const next = (activeStep + 1) % STEPS.length;
    return [
      { index: prev, offset: -1 },
      { index: activeStep, offset: 0 },
      { index: next, offset: +1 },
    ];
  }, [activeStep]);

  const VisualScene = VISUAL_SCENES[activeVisual];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="loading-animation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{ width: "100%" }}
        >
          <AnimationContainer>
            <ContentWrapper>
              {/* Orbital Visual with cycling scenes */}
              <OrbitalContainer>
                {/* Particles */}
                <ParticlesContainer>
                  {particles.map((p, i) => (
                    <Particle
                      key={i}
                      $left={p.left}
                      $delay={p.delay}
                      $duration={p.duration}
                      $size={p.size}
                    />
                  ))}
                </ParticlesContainer>

                {/* Rings */}
                <OrbitalRing $size={224} $duration={60}>
                  <RingDot
                    $size={12}
                    $gradient="linear-gradient(to right, rgb(96, 165, 250), rgb(129, 140, 248))"
                    $shadow="0 0 10px rgba(96, 165, 250, 0.5)"
                    $position="top"
                  />
                </OrbitalRing>

                <OrbitalRing
                  $size={176}
                  $duration={45}
                  $reverse
                  $borderColor="rgba(199, 210, 254, 0.4)"
                >
                  <RingDot
                    $size={8}
                    $gradient="linear-gradient(to right, rgb(129, 140, 248), rgb(168, 85, 247))"
                    $shadow="0 0 10px rgba(129, 140, 248, 0.5)"
                    $position="top"
                  />
                  <RingDot
                    $size={8}
                    $gradient="linear-gradient(to right, rgb(168, 85, 247), rgb(236, 72, 153))"
                    $shadow="0 0 10px rgba(168, 85, 247, 0.5)"
                    $position="right"
                  />
                </OrbitalRing>

                <OrbitalRing
                  $size={128}
                  $duration={30}
                  $borderColor="rgba(221, 214, 254, 0.3)"
                >
                  <RingDot
                    $size={8}
                    $gradient="linear-gradient(to right, rgb(34, 211, 238), rgb(96, 165, 250))"
                    $shadow="0 0 10px rgba(34, 211, 238, 0.5)"
                    $position="bottom"
                  />
                </OrbitalRing>

                {/* Cycling visual scene */}
                <VisualSceneWrapper>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeVisual}
                      initial={{ opacity: 0, scale: 0.7, rotateY: -40 }}
                      animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                      exit={{ opacity: 0, scale: 0.7, rotateY: 40 }}
                      transition={VISUAL_TRANSITION}
                    >
                      <VisualScene />
                    </motion.div>
                  </AnimatePresence>
                </VisualSceneWrapper>
              </OrbitalContainer>

              {/* Step Carousel */}
              <StepCarouselContainer>
                <AnimatePresence initial={false}>
                  {visibleSteps.map(({ index, offset }) => {
                    const step = STEPS[index];
                    const isActive = offset === 0;
                    const Icon = step.icon;

                    return (
                      <motion.div
                        key={index}
                        initial={{ y: 112, z: -100, scale: 0.7, opacity: 0 }}
                        animate={{
                          y: offset * 56,
                          z: offset === 0 ? 0 : -100,
                          scale: offset === 0 ? 1 : 0.85,
                          opacity: offset === 0 ? 1 : 0.35,
                        }}
                        exit={{ y: -112, z: -100, scale: 0.7, opacity: 0 }}
                        transition={{
                          duration: 0.5,
                          ease: [0.23, 1, 0.32, 1],
                        }}
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: "50%",
                          marginTop: -30,
                        }}
                      >
                        <StepItem $isActive={isActive} $isPast={offset < 0}>
                          {isActive && <StepHighlight />}
                          <StepIconWrapper $isActive={isActive}>
                            <Icon />
                          </StepIconWrapper>
                          <StepLabel $isActive={isActive}>
                            {step.label}
                          </StepLabel>
                          {isActive && (
                            <ActiveDots>
                              <ActiveDot $index={0} />
                              <ActiveDot $index={1} />
                              <ActiveDot $index={2} />
                            </ActiveDots>
                          )}
                        </StepItem>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </StepCarouselContainer>

              {/* Progress Dots — tracks visual scene, not step */}
            </ContentWrapper>
          </AnimationContainer>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

LoadingAnimation.displayName = "LoadingAnimation";
